import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {
    getBrowserRegistrationJobProgress,
    getBrowserRegistrationResult,
} from 'src/openapi/admin/apis/BulkUsersBrowserRegistrationApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {BrowserRegistrationItem} from 'src/openapi/admin/models/BrowserRegistrationItem';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateUserDeviceRegistrationResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: BrowserRegistrationItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: BrowserRegistrationItem, index) => (
                <Row key={res.email}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.email}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>{res.oldEnabledStatus ? 'Enabled' : 'Disabled'}</Cell>
                    <Cell>{res.newEnabledStatus ? 'Enabled' : 'Disabled'}</Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<BrowserRegistrationItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            userId: item.userId?.toString(),
                            oldEnabledStatus: item.oldEnabledStatus ? 'Enabled' : 'Disabled',
                            newEnabledStatus: item.newEnabledStatus ? 'Enabled' : 'Disabled',
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={200}>Email</Cell>
                        <Cell width={200}>Old Status</Cell>
                        <Cell width={200}>New Status</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getBrowserRegistrationJobProgress({id}))}
            getResult={() => apiDispatch(getBrowserRegistrationResult({id}))}

            sortComparator={(a, b) => a.email.localeCompare(b.email)}
        />
    );
};
